import React, { Component } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import {
  Eye, Users, FileText, MousePointerClick, ArrowUpRight,
  Globe, Monitor, Smartphone, Tablet, Compass,
} from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import { getCountryName } from "../../utils/country";

import {
  PageHeader,
  Stat,
  StatGroup,
  LineChart,
  DataTable,
  SegmentedControl,
  StatusIndicator,
  Card,
  EmptyState,
  CountryFlag,
  DateRangePicker,
  ArticleTypeLabel,
  ArticleStatusBadge,
} from "../../design-system";

const RANGES = [
  { value: "7d",  label: "7 jours"  },
  { value: "30d", label: "30 jours" },
  { value: "90d", label: "90 jours" },
];

const EVENT_LABELS = {
  cv_download:           "CV téléchargé",
  contact_submit:        "Contact envoyé",
  newsletter_subscribe:  "Abonnement newsletter",
};

const DEVICE_ICONS = {
  desktop: Monitor,
  mobile:  Smartphone,
  tablet:  Tablet,
};

function formatNumber(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`.replace(".0k", "k");
  return String(n);
}

function formatDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

class AnalyticsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      range: "30d",
      customRange: { from: null, to: null },
      loading: true,
      summary: null,
    };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true });
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const { from, to } = this.state.customRange || {};
      const params = from && to
        ? `from=${from.toISOString()}&to=${to.toISOString()}`
        : `range=${this.state.range}`;
      const response = await axios.get(`/api/pulse/summary?${params}`);
      this.setState({ summary: response.data, loading: false });
    } catch (error) {
      console.error("Error loading analytics:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible de charger les statistiques",
        type: "danger",
        insert: "top",
        container: "top-right",
        dismiss: { duration: 3000 },
      });
    }
  };

  setRange = (range) => {
    this.setState({ range, customRange: { from: null, to: null } }, this.load);
  };

  setCustomRange = (customRange) => {
    if (customRange.from && customRange.to) {
      this.setState({ customRange, range: null }, this.load);
    } else {
      this.setState({ customRange });
    }
  };

  renderStats() {
    const { summary } = this.state;
    if (!summary) return null;
    const { totals } = summary;
    return (
      <StatGroup columns="md:grid-cols-2 lg:grid-cols-4">
        <Stat
          variant="ink"
          label="Pages vues"
          value={formatNumber(totals.views)}
          ghost={totals.views}
          icon={<Eye className="w-4 h-4" strokeWidth={1.6} />}
        />
        <Stat
          variant="cream"
          label="Visiteurs uniques"
          value={formatNumber(totals.uniques)}
          ghost={totals.uniques}
          hint="anonyme · rotation 24h"
          icon={<Users className="w-4 h-4" strokeWidth={1.6} />}
        />
        <Stat
          variant="ink"
          label="Articles vus"
          value={formatNumber(totals.articleViews)}
          ghost={totals.articleViews}
          icon={<FileText className="w-4 h-4" strokeWidth={1.6} />}
        />
        <Stat
          variant="cream"
          label="Événements"
          value={formatNumber(totals.events)}
          ghost={totals.events}
          hint="conversions (CV, contact, newsletter)"
          icon={<MousePointerClick className="w-4 h-4" strokeWidth={1.6} />}
        />
      </StatGroup>
    );
  }

  renderChart() {
    const { summary } = this.state;
    if (!summary?.timeseries?.length) return null;
    return (
      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[15px] font-semibold tracking-tight">
            Trafic <span className="font-serif italic font-normal text-mute">— jour par jour.</span>
          </h2>
          <span className="text-[11px] font-mono text-mute uppercase tracking-wider">
            {summary.range}
          </span>
        </div>
        <LineChart
          data={summary.timeseries}
          xKey="date"
          series={[
            { key: "views",   label: "Pages vues",        color: "ink" },
            { key: "uniques", label: "Visiteurs uniques", color: "mute", dashed: true },
          ]}
          height={280}
          formatX={formatDateShort}
        />
      </Card>
    );
  }

  renderTopArticles() {
    const { summary } = this.state;
    const rows = summary?.topArticles || [];
    return (
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-[13px] font-semibold uppercase tracking-wider">Top articles</h3>
          <Link
            to="/dashboard/articles"
            className="text-[11px] font-medium text-mute hover:text-text inline-flex items-center gap-1"
          >
            Tous <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} />
          </Link>
        </div>
        {rows.length === 0 ? (
          <Card><EmptyState title="Aucune vue d'article" description="Personne n'a encore ouvert d'article sur cette période." /></Card>
        ) : (
          <DataTable
            data={rows}
            rowKey="articleId"
            variant="duotone"
            showRowIndex
            columns={[
              {
                key: "title", header: "Article",
                render: (row) => (
                  <div className="min-w-0">
                    <Link
                      to={`/dashboard/articles/${row.articleId}/view`}
                      className="text-sm font-semibold text-text hover:underline underline-offset-2 leading-tight line-clamp-2 block"
                    >
                      {row.title}
                    </Link>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-mute">
                      <ArticleTypeLabel type={row.type} />
                      <span className="text-mute-2">·</span>
                      <ArticleStatusBadge status={row.status} size="sm" />
                    </div>
                  </div>
                ),
              },
              {
                key: "views", header: "Vues", align: "right", width: 100,
                render: (row) => (
                  <span className="font-mono tabular-nums font-semibold">{formatNumber(row.views)}</span>
                ),
              },
            ]}
          />
        )}
      </div>
    );
  }

  renderTopPages() {
    const { summary } = this.state;
    const rows = summary?.topPages || [];
    return (
      <div>
        <h3 className="text-[13px] font-semibold uppercase tracking-wider mb-2">Top pages</h3>
        {rows.length === 0 ? (
          <Card><EmptyState title="Pas encore de vues" /></Card>
        ) : (
          <DataTable
            data={rows}
            rowKey="path"
            variant="duotone"
            showRowIndex
            columns={[
              {
                key: "path", header: "Route",
                render: (row) => (
                  <span className="font-mono text-[12px] text-text truncate block">{row.path}</span>
                ),
              },
              {
                key: "views", header: "Vues", align: "right", width: 100,
                render: (row) => (
                  <span className="font-mono tabular-nums font-semibold">{formatNumber(row.views)}</span>
                ),
              },
            ]}
          />
        )}
      </div>
    );
  }

  renderTopReferrers() {
    const { summary } = this.state;
    const rows = summary?.topReferrers || [];
    return (
      <div>
        <h3 className="text-[13px] font-semibold uppercase tracking-wider mb-2">Sources de trafic</h3>
        {rows.length === 0 ? (
          <Card>
            <div className="px-4 py-6 text-center">
              <Compass className="w-5 h-5 text-mute mx-auto mb-2" strokeWidth={1.6} />
              <p className="text-sm text-mute italic">Trafic direct uniquement sur cette période.</p>
            </div>
          </Card>
        ) : (
          <Card padding="none">
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <li key={r.referrer} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-text font-medium truncate">{r.referrer}</span>
                  <span className="font-mono tabular-nums text-sm text-mute shrink-0 ml-3">
                    {formatNumber(r.views)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  renderEvents() {
    const { summary } = this.state;
    const rows = summary?.events || [];
    return (
      <div>
        <h3 className="text-[13px] font-semibold uppercase tracking-wider mb-2">Conversions</h3>
        {rows.length === 0 ? (
          <Card><EmptyState title="Aucune conversion" description="Aucun CV téléchargé, ni contact envoyé, ni inscription sur cette période." /></Card>
        ) : (
          <Card padding="none">
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <li key={r.name} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-text font-medium">
                    {EVENT_LABELS[r.name] || r.name}
                  </span>
                  <span className="font-mono tabular-nums text-sm text-mute">
                    {formatNumber(r.count)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  renderCountries() {
    const rows = this.state.summary?.countries || [];
    const totalGeo = rows.reduce((s, r) => s + r.views, 0);

    return (
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-[13px] font-semibold uppercase tracking-wider inline-flex items-baseline gap-2">
            Géographie
            {rows.length > 0 && (
              <span className="text-[11px] font-mono normal-case tracking-normal text-mute">
                — {rows.length} pays
              </span>
            )}
          </h3>
        </div>
        {rows.length === 0 ? (
          <Card>
            <EmptyState
              icon={Globe}
              title="Pas encore de pays trackés"
              description="Les visites locales (localhost, IPs privées) ne sont pas géolocalisées."
            />
          </Card>
        ) : (
          <Card padding="none">
            <ul className="grid grid-cols-1 sm:grid-cols-2 divide-y divide-border sm:divide-y-0 sm:[&>li:nth-child(even)]:border-l sm:[&>li:nth-child(even)]:border-border sm:[&>li:nth-child(n+3)]:border-t sm:[&>li]:border-border">
              {rows.map((r) => {
                const pct = totalGeo > 0 ? (r.views / totalGeo) * 100 : 0;
                const fullName = getCountryName(r.country);
                return (
                  <li key={r.country} className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <CountryFlag code={r.country} size="md" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-1.5 leading-tight">
                          <span className="text-sm font-semibold text-text truncate">
                            {fullName}
                          </span>
                          <span className="text-[10.5px] font-mono text-mute-2 uppercase tracking-wider">
                            {r.country}
                          </span>
                        </div>
                        <div className="mt-1.5 h-1 bg-bg-2 rounded-full overflow-hidden">
                          <div className="h-full bg-ink rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono tabular-nums text-sm font-semibold text-text leading-none">
                          {formatNumber(r.views)}
                        </div>
                        <div className="font-mono tabular-nums text-[10.5px] text-mute mt-1">
                          {pct.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  renderBreakdown(title, rows, key, opts = {}) {
    const max = Math.max(1, ...rows.map((r) => r.views));
    return (
      <div>
        <h3 className="text-[13px] font-semibold uppercase tracking-wider mb-2">{title}</h3>
        {rows.length === 0 ? (
          <Card><EmptyState title="—" description="Pas de données." /></Card>
        ) : (
          <Card padding="none">
            <ul className="divide-y divide-border">
              {rows.map((r) => {
                const label = opts.label ? opts.label(r[key]) : r[key];
                const Icon = opts.icon ? opts.icon(r[key]) : null;
                const pct = (r.views / max) * 100;
                return (
                  <li key={r[key] || "_"} className="px-4 py-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-text font-medium inline-flex items-center gap-2">
                        {Icon && <Icon className="w-3.5 h-3.5 text-mute" strokeWidth={1.6} />}
                        {label}
                      </span>
                      <span className="font-mono tabular-nums text-sm text-mute">
                        {formatNumber(r.views)}
                      </span>
                    </div>
                    <div className="h-1 bg-bg-2 rounded-full overflow-hidden">
                      <div className="h-full bg-ink" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  render() {
    const { range, loading, summary } = this.state;

    return (
      <AdminPageShell pageTitle="Analytics">
        <div className="space-y-6 lg:space-y-7">
          <PageHeader
            size="hero"
            eyebrow="— Section ix · Analytics"
            title={<>Le pouls <span className="font-serif italic font-normal align-baseline">du site.</span></>}
            description="Visiteurs, pages vues, articles lus, conversions — collecte anonyme avec rotation quotidienne du hash (pas de cookie, pas de consentement requis)."
            actions={
              <div className="flex items-center gap-2">
                <SegmentedControl
                  value={range || ""}
                  onChange={this.setRange}
                  options={RANGES}
                  size="sm"
                />
                <div className="w-[260px]">
                  <DateRangePicker
                    value={this.state.customRange}
                    onChange={this.setCustomRange}
                    size="sm"
                    placeholder="Période custom…"
                    maxDate={new Date()}
                  />
                </div>
              </div>
            }
          />

          {loading && !summary ? (
            <Card><EmptyState title="Chargement…" /></Card>
          ) : (
            <>
              {this.renderStats()}
              {this.renderChart()}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7">
                {this.renderTopArticles()}
                {this.renderTopPages()}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7">
                {this.renderTopReferrers()}
                {this.renderEvents()}
              </div>

              {this.renderCountries()}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-7">
                {this.renderBreakdown(
                  "Devices",
                  summary?.devices || [],
                  "device",
                  {
                    icon: (v) => DEVICE_ICONS[v] || Monitor,
                    label: (v) => v?.charAt(0).toUpperCase() + v?.slice(1),
                  },
                )}
                {this.renderBreakdown(
                  "Navigateurs",
                  summary?.browsers || [],
                  "browser",
                )}
              </div>

              <p className="text-[11px] font-mono text-mute uppercase tracking-wider text-center pt-2">
                <StatusIndicator tone="muted" size="sm">RGPD-friendly · hash quotidien anonyme · no cookie</StatusIndicator>
              </p>
            </>
          )}
        </div>
      </AdminPageShell>
    );
  }
}

export default AnalyticsPage;
